import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";

const ObrigacoesPadraoModal = ({
  open,
  onClose,
  tiposPadrao,
  setores,
  onSubmit,
  loading,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));

  // Estado para controlar quais tipos estão selecionados
  const [tiposSelecionados, setTiposSelecionados] = useState(new Set());
  const [edicoesTemporarias, setEdicoesTemporarias] = useState({});

  // Função para alternar seleção de um tipo
  const handleToggleTipo = (tipoId) => {
    const novosSelecionados = new Set(tiposSelecionados);
    if (novosSelecionados.has(tipoId)) {
      novosSelecionados.delete(tipoId);
      // Limpar edições temporárias quando desmarcar
      const novasEdicoes = { ...edicoesTemporarias };
      delete novasEdicoes[tipoId];
      setEdicoesTemporarias(novasEdicoes);
    } else {
      novosSelecionados.add(tipoId);
    }
    setTiposSelecionados(novosSelecionados);
  };

  // Função para alternar seleção de todos
  const handleToggleTodos = () => {
    if (tiposSelecionados.size === tiposPadrao.length) {
      setTiposSelecionados(new Set());
      setEdicoesTemporarias({});
    } else {
      setTiposSelecionados(new Set(tiposPadrao.map((tipo) => tipo.id)));
    }
  };

  // Função para editar um campo específico de um tipo
  const handleCampoChange = (tipoId, campo, valor) => {
    setEdicoesTemporarias((prev) => ({
      ...prev,
      [tipoId]: {
        ...prev[tipoId],
        [campo]: valor,
      },
    }));
  };

  // Função para obter o valor final de um campo (com edição ou valor original)
  const obterValor = (tipo, campo) => {
    const edicao = edicoesTemporarias[tipo.id];
    return edicao?.[campo] !== undefined ? edicao[campo] : tipo[campo];
  };

  // Função para submeter
  const handleSubmit = () => {
    const tiposParaCriar = tiposPadrao
      .filter((tipo) => tiposSelecionados.has(tipo.id))
      .map((tipo) => {
        const edicao = edicoesTemporarias[tipo.id] || {};
        return {
          descricao: edicao.descricao || tipo.descricao,
          dia_recorrente: edicao.dia_recorrente || tipo.dia_recorrente,
          mes_recorrente: edicao.mes_recorrente || tipo.mes_recorrente,
          dia_recorrente_real:
            edicao.dia_recorrente_real || tipo.dia_recorrente_real,
          mes_recorrente_real:
            edicao.mes_recorrente_real || tipo.mes_recorrente_real,
          simples_nacional:
            edicao.simples_nacional !== undefined
              ? edicao.simples_nacional
              : tipo.simples_nacional,
          lucro_presumido:
            edicao.lucro_presumido !== undefined
              ? edicao.lucro_presumido
              : tipo.lucro_presumido,
          lucro_real:
            edicao.lucro_real !== undefined
              ? edicao.lucro_real
              : tipo.lucro_real,
          setor_id: edicao.setor_id || "",
        };
      });

    onSubmit(tiposParaCriar);
  };

  // Função para fechar e limpar estado
  const handleClose = () => {
    setTiposSelecionados(new Set());
    setEdicoesTemporarias({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: { minHeight: fullScreen ? "100vh" : "80vh" },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Criar obrigações padrão
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecione e configure os tipos de obrigações que deseja criar no seu
          escritório
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {tiposPadrao.length} tipos cadastrados • {tiposSelecionados.size}{" "}
            selecionados
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      tiposSelecionados.size > 0 &&
                      tiposSelecionados.size < tiposPadrao.length
                    }
                    checked={
                      tiposPadrao.length > 0 &&
                      tiposSelecionados.size === tiposPadrao.length
                    }
                    onChange={handleToggleTodos}
                  />
                </TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="center">Dia recorrente</TableCell>
                <TableCell align="center">Mês recorrente</TableCell>
                <TableCell align="center">Dia recorrente real</TableCell>
                <TableCell align="center">Mês recorrente real</TableCell>
                <TableCell align="center">Simples nacional</TableCell>
                <TableCell align="center">Lucro presumido</TableCell>
                <TableCell align="center">Lucro real</TableCell>
                <TableCell align="center">Setor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tiposPadrao.map((tipo) => (
                <TableRow key={tipo.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={tiposSelecionados.has(tipo.id)}
                      onChange={() => handleToggleTipo(tipo.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      value={obterValor(tipo, "descricao") || ""}
                      onChange={(e) =>
                        handleCampoChange(tipo.id, "descricao", e.target.value)
                      }
                      fullWidth
                      disabled={!tiposSelecionados.has(tipo.id)}
                      placeholder="Descrição"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      value={obterValor(tipo, "dia_recorrente") || ""}
                      onChange={(e) =>
                        handleCampoChange(
                          tipo.id,
                          "dia_recorrente",
                          parseInt(e.target.value) || "",
                        )
                      }
                      disabled={!tiposSelecionados.has(tipo.id)}
                      inputProps={{ min: 1, max: 31 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      value={obterValor(tipo, "mes_recorrente") || ""}
                      onChange={(e) =>
                        handleCampoChange(
                          tipo.id,
                          "mes_recorrente",
                          parseInt(e.target.value) || "",
                        )
                      }
                      disabled={!tiposSelecionados.has(tipo.id)}
                      inputProps={{ min: 1, max: 12 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      value={obterValor(tipo, "dia_recorrente_real") || ""}
                      onChange={(e) =>
                        handleCampoChange(
                          tipo.id,
                          "dia_recorrente_real",
                          parseInt(e.target.value) || "",
                        )
                      }
                      disabled={!tiposSelecionados.has(tipo.id)}
                      inputProps={{ min: 1, max: 31 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      value={obterValor(tipo, "mes_recorrente_real") || ""}
                      onChange={(e) =>
                        handleCampoChange(
                          tipo.id,
                          "mes_recorrente_real",
                          parseInt(e.target.value) || "",
                        )
                      }
                      disabled={!tiposSelecionados.has(tipo.id)}
                      inputProps={{ min: 1, max: 12 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={obterValor(tipo, "simples_nacional") || false}
                      onChange={(e) =>
                        handleCampoChange(
                          tipo.id,
                          "simples_nacional",
                          e.target.checked,
                        )
                      }
                      disabled={!tiposSelecionados.has(tipo.id)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={obterValor(tipo, "lucro_presumido") || false}
                      onChange={(e) =>
                        handleCampoChange(
                          tipo.id,
                          "lucro_presumido",
                          e.target.checked,
                        )
                      }
                      disabled={!tiposSelecionados.has(tipo.id)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={obterValor(tipo, "lucro_real") || false}
                      onChange={(e) =>
                        handleCampoChange(
                          tipo.id,
                          "lucro_real",
                          e.target.checked,
                        )
                      }
                      disabled={!tiposSelecionados.has(tipo.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={obterValor(tipo, "setor_id") || ""}
                        onChange={(e) =>
                          handleCampoChange(tipo.id, "setor_id", e.target.value)
                        }
                        disabled={!tiposSelecionados.has(tipo.id)}
                        displayEmpty
                      >
                        <MenuItem value="">Nenhum</MenuItem>
                        {setores.map((setor) => (
                          <MenuItem key={setor.id} value={setor.id}>
                            {setor.descricao}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || tiposSelecionados.size === 0}
          startIcon={loading && <CircularProgress size={16} />}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
        >
          {loading
            ? "Criando..."
            : `Criar ${tiposSelecionados.size} obrigações`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ObrigacoesPadraoModal;
